// VentasListXL - Sprint 44 Parte 2.
// Migracion XL de src/modules/ventas/list/VentasList.jsx.
// Reusa useVentas + useFacturaElectronica + FEStatusBadge + fmtMoney.
// Solo componentes XL + tokens + motion + dark mode.
// Sin estilos inline. Sin CSS suelto. Sin libs externas.
// Backend intacto.
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import useVentas from '../ventas/hooks/useVentas';
import { fmtMoney } from '../../utils/money';
import FEStatusBadge from '../facturacionElectronica/FEStatusBadge';
import useFacturaElectronica from '../facturacionElectronica/hooks/useFacturaElectronica';

import XLTable from '../../components/xl/XLTable';
import XLBadge from '../../components/xl/XLBadge';
import XLInput from '../../components/xl/XLInput';
import XLSelect from '../../components/xl/XLSelect';
import XLTooltip from '../../components/xl/XLTooltip';

import { useXLTheme } from '../../design/tokens';
import { ensureKeyframes, injectCSS } from '../../design/motion';

const CSS_ID = 'xl-ventas-page-css';
const CSS = [
  '.xl-page { padding: 24px; background: var(--xl-color-surface-alt);',
  '  min-height: 100vh; font-family: var(--xl-font-sans); color: var(--xl-color-text); }',
  '.xl-page__head { display: flex; align-items: flex-start; justify-content: space-between;',
  '  gap: 16px; margin-bottom: 20px; flex-wrap: wrap; }',
  '.xl-page__title { font-size: 22px; font-weight: 600; margin: 0 }',
  '.xl-page__subtitle { font-size: 13px; color: var(--xl-color-text-secondary); margin: 2px 0 0; }',
  '.xl-page__actions { display: flex; gap: 8px; align-items: center }',
  '.xl-btn { font: 500 13px/1 var(--xl-font-sans); padding: 10px 16px;',
  '  border-radius: var(--xl-radius-md); border: 1px solid transparent; cursor: pointer;',
  '  transition: background 120ms cubic-bezier(0,0,0.2,1), border-color 120ms cubic-bezier(0,0,0.2,1); }',
  '.xl-btn:focus-visible { outline: 2px solid var(--xl-color-focus); outline-offset: 2px; }',
  '.xl-btn--primary { background: var(--xl-color-primary); color: var(--xl-color-text-inverse); }',
  '.xl-btn--primary:hover { background: var(--xl-color-primary-hover) }',
  '.xl-btn--ghost { background: transparent; color: var(--xl-color-text-secondary);',
  '  border-color: var(--xl-color-border); }',
  '.xl-btn--ghost:hover { background: var(--xl-color-surface); color: var(--xl-color-text); }',
  '.xl-btn:disabled { opacity: 0.45; cursor: not-allowed }',
  '.xl-filters { display: grid; grid-template-columns: 1fr 220px; gap: 12px;',
  '  background: var(--xl-color-surface); border: 1px solid var(--xl-color-border);',
  '  border-radius: var(--xl-radius-lg); padding: 16px; margin-bottom: 16px;',
  '  box-shadow: var(--xl-shadow-sm); }',
  '@media (max-width: 600px) { .xl-filters { grid-template-columns: 1fr } }',
  '.xl-pager { display: flex; align-items: center; justify-content: space-between;',
  '  gap: 12px; padding: 12px 4px; margin-top: 12px;',
  '  color: var(--xl-color-text-secondary); font-size: 13px; }',
  '.xl-pager__group { display: flex; gap: 8px }',
  '.xl-muted { color: var(--xl-color-text-muted) }',
  '.xl-mono { font-family: var(--xl-font-mono) }',
  '.xl-mono--medium { font-family: var(--xl-font-mono); font-weight: 500 }',
].join('\n');

const ESTADO_VARIANT = { borrador: 'neutral', emitida: 'success', anulada: 'danger' };
const ESTADO_LABEL = { borrador: 'Borrador', emitida: 'Emitida', anulada: 'Anulada' };
const FILTRO_ESTADOS = [
  { value: '',         label: 'Todos los estados' },
  { value: 'borrador', label: 'Borrador' },
  { value: 'emitida',  label: 'Emitida' },
  { value: 'anulada',  label: 'Anulada' },
];

function FECell({ venta }) {
  const { fe } = useFacturaElectronica(
    venta.estado === 'emitida' ? venta.id : null
  );
  if (venta.estado !== 'emitida') return <span className="xl-muted">—</span>;
  return <FEStatusBadge fe={fe} />;
}

function useDebouncedSetter(setter, delay) {
  const [local, setLocal] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setter(local), delay);
    return () => clearTimeout(t);
  }, [local, delay, setter]);
  return [local, setLocal];
}

export default function VentasListXL() {
  const navigate = useNavigate();
  const [theme, setTheme] = useXLTheme();
  const vx = useVentas();

  useEffect(() => { ensureKeyframes(); injectCSS(CSS_ID, CSS); }, []);
  const [searchLocal, setSearchLocal] = useDebouncedSetter(vx.setQ, 300);

  const columns = useMemo(() => ([
    { key: 'numero', label: '#',
      render: (r) => <span className="xl-mono--medium">{r.numero}</span> },
    { key: 'cliente', label: 'Cliente',
      render: (r) => (r.cliente && r.cliente.nombre) || '—' },
    { key: 'fecha', label: 'Fecha',
      render: (r) => r.fecha || '—' },
    { key: 'total', label: 'Total', align: 'right',
      render: (r) => (
        <span className="xl-mono">{fmtMoney(r.total, r.moneda)}</span>
      ) },
    { key: 'estado', label: 'Estado',
      render: (r) => (
        <XLBadge variant={ESTADO_VARIANT[r.estado] || 'neutral'} dot>
          {ESTADO_LABEL[r.estado] || r.estado}
        </XLBadge>
      ) },
    { key: 'fe', label: 'FE DIAN',
      render: (r) => <FECell venta={r} /> },
  ]), []);

  const totalPages = Math.max(1, Math.ceil((vx.total || 0) / (vx.perPage || 25)));
  const canPrev = vx.page > 1;
  const canNext = vx.page < totalPages;
  const nextTheme = theme === 'dark' ? 'light' : 'dark';
  const themeLabel = theme === 'dark' ? 'Tema claro' : 'Tema oscuro';

  return (
    <div className="xl-page">
      <header className="xl-page__head">
        <div>
          <h1 className="xl-page__title">Ventas</h1>
          <p className="xl-page__subtitle">Facturas de venta (emite stock al confirmar).</p>
        </div>
        <div className="xl-page__actions">
          <XLTooltip label={themeLabel}>
            <button type="button" className="xl-btn xl-btn--ghost"
              onClick={() => setTheme(nextTheme)} aria-label={themeLabel}>
              {theme === 'dark' ? 'Claro' : 'Oscuro'}
            </button>
          </XLTooltip>
          <button type="button" className="xl-btn xl-btn--primary"
            onClick={() => navigate('/ventas/nueva')}>
            + Nueva venta
          </button>
        </div>
      </header>

      <section className="xl-filters" aria-label="Filtros de ventas">
        <XLInput label="Buscar" type="search" placeholder="Numero o cliente..."
          value={searchLocal} onChange={(e) => setSearchLocal(e.target.value)} />
        <XLSelect label="Estado" value={vx.estado}
          onChange={(e) => { vx.setEstado(e.target.value); vx.setPage(1); }}
          options={FILTRO_ESTADOS} />
      </section>

      <XLTable ariaLabel="Lista de ventas" columns={columns}
        data={vx.items} loading={vx.loading}
        onRowClick={(r) => navigate('/ventas/' + r.id)} rowKey="id"
        emptyMessage="Sin ventas para estos filtros." />

      <div className="xl-pager">
        <span>
          {vx.total
            ? ('Pagina ' + vx.page + ' de ' + totalPages + ' - ' + vx.total + ' resultados')
            : 'Sin resultados'}
        </span>
        <span className="xl-pager__group">
          <button type="button" className="xl-btn xl-btn--ghost"
            disabled={!canPrev || vx.loading}
            onClick={() => vx.setPage(vx.page - 1)}>
            Anterior
          </button>
          <button type="button" className="xl-btn xl-btn--ghost"
            disabled={!canNext || vx.loading}
            onClick={() => vx.setPage(vx.page + 1)}>
            Siguiente
          </button>
        </span>
      </div>
    </div>
  );
}
